// ============================================================
// COREBANK — KYC / ONBOARDING ENGINE
// ============================================================
// Customer onboarding workflow: data collection → screening →
// RM review → approval. Implements MLR 2017 UBO requirements,
// state machine with valid transitions, and periodic review scheduling.
//
// Depends on: EventBus (injected)
// Used by: integration-tests.js, corebank-shell.jsx
// ============================================================

import { requireEventBus, requireString, requireEnum } from '../validate.js';

let _seq = 0;
function genId(pfx = '') {
  _seq++;
  return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`;
}

// ---- Types ----

const CustomerType = { CORPORATE: 'CORPORATE', SOLE_TRADER: 'SOLE_TRADER', PARTNERSHIP: 'PARTNERSHIP', TRUST: 'TRUST' };
const RiskRating = { LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH', PROHIBITED: 'PROHIBITED' };

const KYCStatus = {
  DATA_COLLECTION: 'DATA_COLLECTION',
  SCREENING: 'SCREENING',
  RM_REVIEW: 'RM_REVIEW',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  REVIEW_DUE: 'REVIEW_DUE',
  UNDER_REVIEW: 'UNDER_REVIEW',
  CLOSED: 'CLOSED',
};

const KYC_TRANSITIONS = {
  [KYCStatus.DATA_COLLECTION]: [KYCStatus.SCREENING, KYCStatus.REJECTED],
  [KYCStatus.SCREENING]: [KYCStatus.RM_REVIEW, KYCStatus.REJECTED, KYCStatus.DATA_COLLECTION],
  [KYCStatus.RM_REVIEW]: [KYCStatus.APPROVED, KYCStatus.REJECTED, KYCStatus.DATA_COLLECTION, KYCStatus.SCREENING],
  [KYCStatus.APPROVED]: [KYCStatus.REVIEW_DUE, KYCStatus.CLOSED],
  [KYCStatus.REJECTED]: [KYCStatus.DATA_COLLECTION],
  [KYCStatus.REVIEW_DUE]: [KYCStatus.UNDER_REVIEW, KYCStatus.CLOSED],
  [KYCStatus.UNDER_REVIEW]: [KYCStatus.APPROVED, KYCStatus.REJECTED, KYCStatus.CLOSED, KYCStatus.DATA_COLLECTION, KYCStatus.SCREENING],
  [KYCStatus.CLOSED]: [],
};

const ScreeningType = { SANCTIONS: 'SANCTIONS', PEP: 'PEP', ADVERSE_MEDIA: 'ADVERSE_MEDIA' };
const ScreeningResult = { CLEAR: 'CLEAR', MATCH: 'MATCH', POSSIBLE_MATCH: 'POSSIBLE_MATCH' };
const REVIEW_PERIODS = { [RiskRating.HIGH]: 365, [RiskRating.MEDIUM]: 730, [RiskRating.LOW]: 1095 };

// ---- Records ----

class CustomerRecord {
  constructor({ type, legalName, registrationNumber, registeredAddress, tradingAddresses, sicCodes, incorporationDate, metadata }) {
    this.id = genId('cust_');
    this.type = type;
    this.legalName = legalName;
    this.registrationNumber = registrationNumber || null;
    this.registeredAddress = registeredAddress || null;
    this.tradingAddresses = tradingAddresses || [];
    this.sicCodes = sicCodes || [];
    this.incorporationDate = incorporationDate || null;
    this.metadata = metadata || {};
    this.individuals = new Map();
    this.ownershipStructure = [];
    this.accountIds = [];
    this.createdAt = new Date().toISOString();
    this.updatedAt = new Date().toISOString();
  }
}

class IndividualRecord {
  constructor({ name, dateOfBirth, nationality, residentialAddress, role, ownershipPct, sourceOfWealth }) {
    this.id = genId('ind_');
    this.name = name;
    this.dateOfBirth = dateOfBirth || null;
    this.nationality = nationality || null;
    this.residentialAddress = residentialAddress || null;
    this.role = role;
    this.ownershipPct = ownershipPct || 0;
    this.sourceOfWealth = sourceOfWealth || null;
    this.screeningResults = [];
    this.idVerificationStatus = null;
  }
}

// ---- Engine ----

class KYCEngine {
  constructor(eventBus) {
    requireEventBus(eventBus);
    this.cases = new Map();
    this.customers = new Map();
    this.eventBus = eventBus;
  }

  createCustomer(data) {
    requireString(data.legalName, 'legalName');
    requireEnum(data.type, CustomerType, 'Customer type');
    const customer = new CustomerRecord(data);
    this.customers.set(customer.id, customer);
    const kycCase = {
      id: genId('kyc_'),
      customerId: customer.id,
      status: KYCStatus.DATA_COLLECTION,
      statusHistory: [{ status: KYCStatus.DATA_COLLECTION, at: new Date().toISOString(), by: 'system' }],
      dataCompleteness: {},
      screeningResults: [],
      rmAssessment: null,
      riskRating: null,
      nextReviewDate: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.cases.set(customer.id, kycCase);
    this.eventBus.publish({
      type: 'CustomerCreated',
      entity_id: customer.id,
      data: { legalName: customer.legalName, type: customer.type },
    });
    return { customer, kycCase };
  }

  getCustomer(id) { return this.customers.get(id); }
  getAllCustomers() { return [...this.customers.values()]; }
  getKYCCase(customerId) { return this.cases.get(customerId); }
  getAllCases() { return [...this.cases.values()]; }

  addIndividual(customerId, data) {
    const customer = this.customers.get(customerId);
    if (!customer) throw new Error('Customer not found');
    const newPct = data.ownershipPct || 0;
    if (newPct > 100) throw new Error('Individual ownership percentage cannot exceed 100%');
    if (newPct > 0) {
      const currentTotal = Array.from(customer.individuals.values()).reduce((sum, ind) => sum + (ind.ownershipPct || 0), 0);
      if (currentTotal + newPct > 100) {
        throw new Error(`Total ownership would be ${currentTotal + newPct}% (current: ${currentTotal}%, new: ${newPct}%). Cannot exceed 100%`);
      }
    }
    const individual = new IndividualRecord(data);
    customer.individuals.set(individual.id, individual);
    customer.updatedAt = new Date().toISOString();
    return individual;
  }

  setOwnershipStructure(customerId, structure) {
    const customer = this.customers.get(customerId);
    if (!customer) throw new Error('Customer not found');
    customer.ownershipStructure = structure;
    customer.updatedAt = new Date().toISOString();
  }

  confirmNoUBOs(customerId, confirmed) {
    const customer = this.customers.get(customerId);
    if (!customer) throw new Error('Customer not found');
    customer.uboConfirmedNone = !!confirmed;
    customer.updatedAt = new Date().toISOString();
    return { customerId, uboConfirmedNone: customer.uboConfirmedNone };
  }

  checkDataCompleteness(customerId) {
    const customer = this.customers.get(customerId);
    if (!customer) throw new Error('Customer not found');
    const issues = [];

    if (!customer.legalName) issues.push('Legal name required');
    if (customer.type === CustomerType.CORPORATE) {
      if (!customer.registrationNumber) issues.push('Registration number required');
      if (!customer.registeredAddress) issues.push('Registered address required');
    }

    const directors = Array.from(customer.individuals.values())
      .filter(i => i.role === 'DIRECTOR' || i.role === 'DIRECTOR_UBO');
    if (directors.length === 0) issues.push('At least one director required');

    const ubos = Array.from(customer.individuals.values()).filter(i => i.ownershipPct >= 25);
    if (ubos.length === 0 && customer.type === CustomerType.CORPORATE) {
      const has25PctHolder = customer.ownershipStructure.some(o => o.ownershipPct >= 25);
      if (!has25PctHolder && !customer.uboConfirmedNone) issues.push('UBOs with 25%+ ownership must be identified (or confirmed none exist)');
    }

    for (const [, ind] of customer.individuals) {
      if (!ind.name) issues.push(`Individual ${ind.id}: name required`);
      if (!ind.dateOfBirth) issues.push(`Individual ${ind.name || ind.id}: date of birth required`);
      if (!ind.nationality) issues.push(`Individual ${ind.name || ind.id}: nationality required`);
    }

    const kycCase = this.cases.get(customerId);
    if (kycCase) {
      kycCase.dataCompleteness = { complete: issues.length === 0, issues, checkedAt: new Date().toISOString() };
    }
    return { complete: issues.length === 0, issues };
  }

  submitForScreening(customerId, by) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    // If already in SCREENING (e.g. retry after failed screening), skip transition
    if (kycCase.status === KYCStatus.SCREENING) return kycCase;
    const completeness = this.checkDataCompleteness(customerId);
    if (!completeness.complete) throw new Error(`Data incomplete: ${completeness.issues.join('; ')}`);
    this._transition(customerId, KYCStatus.SCREENING, by);
    return kycCase;
  }

  runScreening(customerId, screeningConfig = {}) {
    const customer = this.customers.get(customerId);
    const kycCase = this.cases.get(customerId);
    if (!customer || !kycCase) throw new Error('Customer not found');
    if (kycCase.status !== KYCStatus.SCREENING) throw new Error('Not in SCREENING status');

    const results = [];
    for (const [, ind] of customer.individuals) {
      for (const type of [ScreeningType.SANCTIONS, ScreeningType.PEP, ScreeningType.ADVERSE_MEDIA]) {
        let result = ScreeningResult.CLEAR;
        if (type === ScreeningType.SANCTIONS && (screeningConfig.sanctionsMatch === ind.id || ind.name?.includes('[SANCTIONS]'))) result = ScreeningResult.MATCH;
        if (type === ScreeningType.PEP && (screeningConfig.pepMatch === ind.id || ind.name?.includes('[PEP]'))) result = ScreeningResult.MATCH;
        if (type === ScreeningType.ADVERSE_MEDIA && (screeningConfig.adverseMediaMatch === ind.id || ind.name?.includes('[ADVERSE]'))) result = ScreeningResult.POSSIBLE_MATCH;
        const sr = {
          individualId: ind.id, individualName: ind.name, type, result,
          checkedAt: new Date().toISOString(),
          details: result !== ScreeningResult.CLEAR ? `${type} flag on ${ind.name}` : null,
        };
        results.push(sr);
        ind.screeningResults.push(sr);
      }
    }
    kycCase.screeningResults = results;
    const hasMatch = results.some(r => r.result === ScreeningResult.MATCH);
    const hasPossibleMatch = results.some(r => r.result === ScreeningResult.POSSIBLE_MATCH);

    this.eventBus.publish({
      type: 'ScreeningCompleted',
      entity_id: kycCase.id,
      data: { customerId, hasMatch, hasPossibleMatch, resultCount: results.length },
    });
    return { results, hasMatch, hasPossibleMatch };
  }

  returnToDataCollection(customerId, by, reason) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    if (kycCase.status === KYCStatus.DATA_COLLECTION) return kycCase;
    const allowed = [KYCStatus.SCREENING, KYCStatus.RM_REVIEW, KYCStatus.UNDER_REVIEW, KYCStatus.REJECTED];
    if (!allowed.includes(kycCase.status)) throw new Error(`Cannot return to data collection from ${kycCase.status}`);
    this._transition(customerId, KYCStatus.DATA_COLLECTION, by, reason || 'Returned to data collection to fix data issues');
    kycCase.screeningResults = [];
    return kycCase;
  }

  returnToScreening(customerId, by, reason) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    if (kycCase.status === KYCStatus.SCREENING) return kycCase;
    const allowed = [KYCStatus.RM_REVIEW, KYCStatus.UNDER_REVIEW];
    if (!allowed.includes(kycCase.status)) throw new Error(`Cannot return to screening from ${kycCase.status}`);
    this._transition(customerId, KYCStatus.SCREENING, by, reason || 'Returned to screening for re-screening');
    return kycCase;
  }

  submitForRMReview(customerId, by) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    if (kycCase.status === KYCStatus.RM_REVIEW) return kycCase;
    if (kycCase.status !== KYCStatus.SCREENING) throw new Error('Must be in SCREENING status');
    if (kycCase.screeningResults.length === 0) throw new Error('Screening must be run before RM review');
    this._transition(customerId, KYCStatus.RM_REVIEW, by);
    return kycCase;
  }

  recordRMAssessment(customerId, { rmId, businessUnderstanding, sourceOfWealth, expectedActivity, concerns, mitigants, recommendation, riskRating }) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    const allowedStatuses = [KYCStatus.RM_REVIEW, KYCStatus.UNDER_REVIEW];
    if (kycCase.status === KYCStatus.APPROVED && recommendation === 'APPROVE') return kycCase;
    if (kycCase.status === KYCStatus.REJECTED && recommendation === 'REJECT') return kycCase;
    if (!allowedStatuses.includes(kycCase.status)) {
      throw new Error(`Cannot record assessment: case is in ${kycCase.status} status (expected RM_REVIEW or UNDER_REVIEW)`);
    }
    if (!rmId) throw new Error('RM ID required');
    if (!recommendation || !['APPROVE', 'REJECT', 'RETURN_FOR_INFO'].includes(recommendation)) {
      throw new Error('Recommendation must be APPROVE, REJECT, or RETURN_FOR_INFO');
    }
    if (!riskRating || !Object.values(RiskRating).includes(riskRating)) {
      throw new Error('Valid risk rating required');
    }

    kycCase.rmAssessment = {
      rmId, businessUnderstanding, sourceOfWealth, expectedActivity,
      concerns, mitigants, recommendation, riskRating,
      recordedAt: new Date().toISOString(),
    };
    kycCase.riskRating = riskRating;

    this.eventBus.publish({
      type: 'RMAssessmentRecorded',
      entity_id: kycCase.id,
      data: { customerId, recommendation, riskRating, rmId },
    });

    if (recommendation === 'APPROVE' && riskRating !== RiskRating.PROHIBITED) {
      this._transition(customerId, KYCStatus.APPROVED, rmId, 'RM approved');
      kycCase.nextReviewDate = this._calculateNextReview(riskRating);
    } else if (recommendation === 'REJECT' || riskRating === RiskRating.PROHIBITED) {
      this._transition(customerId, KYCStatus.REJECTED, rmId,
        riskRating === RiskRating.PROHIBITED ? 'Prohibited risk rating' : 'RM rejected');
    } else if (recommendation === 'RETURN_FOR_INFO') {
      this._transition(customerId, KYCStatus.DATA_COLLECTION, rmId, 'Returned for additional information');
    }
    return kycCase;
  }

  getReviewsDue(asOfDate) {
    const due = [];
    for (const [custId, kycCase] of this.cases) {
      if (kycCase.status !== KYCStatus.APPROVED) continue;
      if (kycCase.nextReviewDate && new Date(kycCase.nextReviewDate) <= asOfDate) {
        due.push({
          customerId: custId,
          legalName: this.customers.get(custId)?.legalName,
          riskRating: kycCase.riskRating,
          nextReviewDate: kycCase.nextReviewDate,
        });
      }
    }
    return due;
  }

  triggerReview(customerId, by) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    if (kycCase.status === KYCStatus.REVIEW_DUE) return kycCase;
    if (kycCase.status !== KYCStatus.APPROVED) throw new Error('Must be APPROVED to trigger review');
    this._transition(customerId, KYCStatus.REVIEW_DUE, by, 'Periodic review due');
    return kycCase;
  }

  startReview(customerId, by) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    if (kycCase.status === KYCStatus.UNDER_REVIEW) return kycCase;
    this._transition(customerId, KYCStatus.UNDER_REVIEW, by, 'Review started');
    return kycCase;
  }

  completeReview(customerId, { rmId, riskRating, recommendation }) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase || kycCase.status !== KYCStatus.UNDER_REVIEW) throw new Error('Must be UNDER_REVIEW');
    kycCase.riskRating = riskRating;
    if (recommendation === 'APPROVE') {
      this._transition(customerId, KYCStatus.APPROVED, rmId, 'Review passed');
      kycCase.nextReviewDate = this._calculateNextReview(riskRating);
    } else {
      this._transition(customerId, KYCStatus.REJECTED, rmId, 'Failed periodic review');
    }
    return kycCase;
  }

  isApproved(customerId) {
    return this.cases.get(customerId)?.status === KYCStatus.APPROVED;
  }

  _calculateNextReview(riskRating) {
    const days = REVIEW_PERIODS[riskRating] || 1095;
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString();
  }

  _transition(customerId, newStatus, by, reason) {
    const kycCase = this.cases.get(customerId);
    if (!kycCase) throw new Error('KYC case not found');
    // Idempotent: if already in the target state, skip
    if (kycCase.status === newStatus) return kycCase;
    if (!(KYC_TRANSITIONS[kycCase.status] || []).includes(newStatus)) {
      throw new Error(`Invalid KYC transition: ${kycCase.status}→${newStatus}`);
    }
    const oldStatus = kycCase.status;
    kycCase.status = newStatus;
    kycCase.updatedAt = new Date().toISOString();
    kycCase.statusHistory.push({ status: newStatus, at: kycCase.updatedAt, by, reason });
    this.eventBus.publish({
      type: 'KYCStatusChanged',
      entity_id: kycCase.id,
      data: { customerId, from: oldStatus, to: newStatus, by, reason },
    });
    return kycCase;
  }
}


// ============================================================
// EXPORTS
// ============================================================

export {
  CustomerType, RiskRating, KYCStatus, KYC_TRANSITIONS,
  ScreeningType, ScreeningResult, REVIEW_PERIODS,
  CustomerRecord, IndividualRecord, KYCEngine,
};
